/**
 * The summary after an import (#229, Addendum No. 4, Point 19).
 *
 * Paco asked for it on 2026-11-03: once the documents are created, a window in the centre of the
 * screen says which were created and which were not, and the user acknowledges it with «Aceptar».
 * `ImportScreen` opens it on a commit it sees finish. The rows are read again here, by status,
 * rather than taken from the review table, which may hold a filter.
 *
 * A row not created is an `ERROR` row: one the upload refused, or one the commit failed. Its
 * reason is its first blocking error, with the field named as the document prints it, or else the
 * commit's own message. A created document links to its own screen, which for a route sheet is not
 * a DeCA's. The rows endpoint sends 200 at most; the rest are counted, and the table has them all.
 */

import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { ImportJob, ImportRow } from '@deca/api'

import { useImportRows } from '../app/queries.ts'
import { Dialog } from '../ui/Dialog.tsx'
import { Button, LoadingRows, Notice } from '../ui/primitives.tsx'

export function ImportSummaryDialog({ job, onClose }: { job: ImportJob; onClose: () => void }) {
  const { t } = useTranslation()
  const created = useImportRows(job.id, 'CREATED')
  const failed = useImportRows(job.id, 'ERROR')
  const passenger = job.kind === 'PASSENGER'

  const createdRows = created.data?.data ?? []
  const failedRows = failed.data?.data ?? []
  const createdTotal = created.data?.meta.total ?? createdRows.length
  const failedTotal = failed.data?.meta.total ?? failedRows.length
  // A draft is sent without a link, so the rows themselves say whether the PDFs were generated.
  const drafts = createdRows.length > 0 && createdRows.every((row) => !row.directDownloadUrl)
  const createdLabel = t(
    drafts
      ? 'import.summary.createdDrafts'
      : passenger
        ? 'import.summary.createdSheets'
        : 'import.summary.createdDecas',
    { count: createdTotal },
  )
  const failedLabel = t('import.summary.notCreated', { count: failedTotal })

  return (
    <Dialog
      open
      title={t('import.summary.title')}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t('import.summary.accept')}
        </Button>
      }
    >
      {job.status === 'FAILED' ? (
        <Notice tone="danger">{job.error ?? t('import.summary.failed')}</Notice>
      ) : null}

      {created.isLoading || failed.isLoading ? (
        <LoadingRows rows={3} columns={1} />
      ) : (
        <div className="stack">
          <div className="stack-sm">
            <h3>{createdLabel}</h3>
            {createdRows.length > 0 ? (
              <ul className="import-summary__documents" aria-label={createdLabel}>
                {createdRows.map((row) => (
                  <li key={row.rowNumber}>
                    <DocumentLink row={row} passenger={passenger} />
                  </li>
                ))}
              </ul>
            ) : null}
            <Remainder count={createdTotal - createdRows.length} />
          </div>

          {failedTotal > 0 ? (
            <div className="stack-sm">
              <h3>{failedLabel}</h3>
              <ul className="import-summary__reasons" aria-label={failedLabel}>
                {failedRows.map((row) => (
                  <li key={row.rowNumber} className="small">
                    <strong>{t('import.summary.row', { row: row.rowNumber })}</strong>:{' '}
                    {reasonOf(row) ?? t('import.summary.noReason')}
                  </li>
                ))}
              </ul>
              <Remainder count={failedTotal - failedRows.length} />
            </div>
          ) : null}
        </div>
      )}
    </Dialog>
  )
}

function DocumentLink({ row, passenger }: { row: ImportRow; passenger: boolean }) {
  if (!row.documentId) return <span className="tabular">{row.documentNumber}</span>
  return passenger ? (
    <Link to="/hojas-de-ruta/$sheetId" params={{ sheetId: row.documentId }} className="tabular">
      {row.documentNumber}
    </Link>
  ) : (
    <Link to="/decas/$decaId" params={{ decaId: row.documentId }} className="tabular">
      {row.documentNumber}
    </Link>
  )
}

/** The rows past the endpoint's page of 200, as a count. */
function Remainder({ count }: { count: number }) {
  const { t } = useTranslation()
  return count > 0 ? <p className="small muted">{t('import.summary.more', { count })}</p> : null
}

/** A row's first reason not to have been created: its first blocking error, or the commit's message. */
function reasonOf(row: ImportRow): string | undefined {
  const errors = [...(row.partyErrors ?? []).flatMap((party) => party.fields), ...(row.fieldErrors ?? [])]
  const first = errors.find((error) => error.severity !== 'WARNING')
  return first ? `${first.labelEs} — ${first.message}` : row.errorMessage
}
